import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  Shield, Mail, Lock, Loader2, CheckCircle2, XCircle, Building2, ArrowRight,
  Eye, EyeOff
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

// ==================== VALIDAÇÃO DE SENHA FORTE ====================
const validatePasswordStrength = (password) => {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password)
  };
  
  return {
    isValid: Object.values(checks).every(v => v),
    checks
  };
};

export default function ProviderRegister() {
  const navigate = useNavigate();
  const { user, login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    authorizeBusinessDisplay: false,
  });

  const BUSINESS_AUTHORIZATION_VERSION = '1.0';

  // Redirect se já logado como provider
  useEffect(() => {
    if (user && user.user_type === 'provider') {
      navigate('/provider/onboarding');
    }
  }, [user, navigate]);

  // Processar callback do Google OAuth — REMOVIDO (Fev/2026)
  // Decisão de produto: prestadores não usam Google Auth.

  const handlePasswordChange = (value) => {
    setForm({ ...form, password: value });
    if (value.length > 0) {
      setPasswordStrength(validatePasswordStrength(value));
    } else {
      setPasswordStrength(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!form.email || !form.email.includes('@')) {
      toast.error('E-mail inválido');
      return;
    }
    
    if (!passwordStrength?.isValid) {
      toast.error('A senha não atende aos requisitos mínimos');
      return;
    }
    
    if (form.password !== form.confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }

    if (!form.authorizeBusinessDisplay) {
      toast.error('Você precisa autorizar a exibição das informações do seu lava-jato para concluir o cadastro');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/provider/onboarding/step1`, {
        email: form.email,
        password: form.password,
        confirm_password: form.confirmPassword,
        authorize_business_display: form.authorizeBusinessDisplay,
        business_authorization_version: BUSINESS_AUTHORIZATION_VERSION,
      });
      
      // Salvar token e redirecionar para próxima etapa
      login(res.data.token, { id: res.data.user_id, user_type: 'provider' });
      toast.success(res.data.message);
      navigate('/provider/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  // handleGoogleLogin REMOVIDO (Fev/2026) — prestadores não usam Google Auth.

  // googleLoading splash REMOVIDO — Google Auth não está mais disponível para prestadores.

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg p-8">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div className="bg-primary/10 p-3 rounded-full">
            <Building2 className="w-10 h-10 text-primary" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-center mb-1">Cadastro de Prestador</h1>
        <p className="text-center text-muted-foreground mb-2">JATOO - Lava-Jato</p>
        
        {/* Info CNPJ obrigatório */}
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <Building2 className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            <strong>Atenção:</strong> O cadastro de prestadores é exclusivo para empresas (CNPJ).
            Você precisará informar o CNPJ na próxima etapa.
          </AlertDescription>
        </Alert>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-primary">Etapa 1 de 6</span>
            <span className="text-xs text-muted-foreground">Criar Conta</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: '16%' }} />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">E-mail *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="email@empresa.com"
                className="pl-10"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="register-email-input"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Este será o e-mail de acesso e notificações
            </p>
          </div>

          <div>
            <Label htmlFor="password">Senha *</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Senha forte"
                className={`pl-10 pr-10 ${passwordStrength?.isValid ? 'border-green-500' : passwordStrength?.isValid === false ? 'border-red-500' : ''}`}
                value={form.password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                required
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirmar Senha *</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Repita a senha"
                className={`pr-10 ${form.confirmPassword && form.password !== form.confirmPassword ? 'border-red-500' : form.confirmPassword && form.password === form.confirmPassword ? 'border-green-500' : ''}`}
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                required
                data-testid="register-confirm-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Indicador de força da senha */}
          {passwordStrength && (
            <div className="space-y-2 p-3 bg-slate-50 rounded-lg border">
              <p className="text-xs font-medium text-slate-700">Requisitos da senha:</p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className={`flex items-center gap-1 ${passwordStrength.checks.minLength ? 'text-green-600' : 'text-red-500'}`}>
                  {passwordStrength.checks.minLength ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  Mínimo 8 caracteres
                </div>
                <div className={`flex items-center gap-1 ${passwordStrength.checks.hasUppercase ? 'text-green-600' : 'text-red-500'}`}>
                  {passwordStrength.checks.hasUppercase ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  Letra maiúscula
                </div>
                <div className={`flex items-center gap-1 ${passwordStrength.checks.hasLowercase ? 'text-green-600' : 'text-red-500'}`}>
                  {passwordStrength.checks.hasLowercase ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  Letra minúscula
                </div>
                <div className={`flex items-center gap-1 ${passwordStrength.checks.hasNumber ? 'text-green-600' : 'text-red-500'}`}>
                  {passwordStrength.checks.hasNumber ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  Número
                </div>
                <div className={`flex items-center gap-1 col-span-2 ${passwordStrength.checks.hasSpecial ? 'text-green-600' : 'text-red-500'}`}>
                  {passwordStrength.checks.hasSpecial ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                  Caractere especial (!@#$%...)
                </div>
              </div>
            </div>
          )}

          {/* Autorização legal — exibição de informações comerciais para clientes */}
          <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <input
              id="authorize-business-display"
              type="checkbox"
              checked={form.authorizeBusinessDisplay}
              onChange={(e) => setForm({ ...form, authorizeBusinessDisplay: e.target.checked })}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-primary focus:ring-2 focus:ring-primary"
              data-testid="provider-business-authorization-checkbox"
              required
            />
            <Label
              htmlFor="authorize-business-display"
              className="text-xs leading-relaxed text-slate-700 cursor-pointer"
              data-testid="provider-business-authorization-label"
            >
              Autorizo a JATOO a exibir as informações do meu lava-jato (nome fantasia,
              endereço, horários e serviços) aos clientes da plataforma para fins de
              localização do estabelecimento e agendamento de serviços.
            </Label>
          </div>

          <Button 
            type="submit" 
            className="w-full h-12" 
            disabled={loading || !passwordStrength?.isValid || !form.authorizeBusinessDisplay}
            data-testid="register-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Criando conta...
              </>
            ) : (
              <>
                Continuar
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <Button variant="link" onClick={() => navigate('/provider/login')} className="text-muted-foreground">
            Já tem conta? Faça login
          </Button>
          <br/>
          <Button variant="link" onClick={() => navigate('/')} className="text-muted-foreground text-xs">
            Voltar ao início
          </Button>
        </div>
      </Card>
    </div>
  );
}
